import { useMemo } from 'react'
import { User, UserAssociation } from '../api/users'
import { Organization } from '../api/organizations'
import { getWorkerAndOrganizationAssociation } from '../utils/users'
import useStore from '../store'

/**
 * Hook to determine if a worker is internal to an organization
 * A worker is considered internal if they have an association with the organization
 *
 * @param worker - The worker/user to check (can be undefined)
 * @param organization - Optional organization (defaults to current organization from store)
 * @returns Object with isInternalWorker boolean and association
 */
export const useIsInternalWorker = (
  worker: User | undefined,
  organization?: Organization,
): { isInternalWorker: boolean; association: UserAssociation | undefined } => {
  const currentOrganization = useStore(state => state.session.currentOrganization)
  const targetOrganization = organization || currentOrganization

  return useMemo(() => {
    if (!worker || !targetOrganization) {
      return {
        isInternalWorker: false,
        association: undefined,
      }
    }

    const association = getWorkerAndOrganizationAssociation(worker, targetOrganization)
    const isInternalWorker = Boolean(association)

    return {
      isInternalWorker,
      association,
    }
  }, [worker, targetOrganization])
}
